import { useRef, useCallback } from 'react';

// Global shared state to persist across component remounts
const globalLockedKeys = new Set<string>();
const globalExecutionHistory = new Map<string, number>();

/**
 * A hook that provides a guard to prevent rapid re-execution of async actions.
 * Primarily used to handle React StrictMode double-invocations in development
 * while allowing fresh executions on real remounts or navigation.
 * 
 * Supports an optional key for global deduplication (persists across remounts).
 * 
 * @param gracePeriodMs - The time window (in ms) during which subsequent calls are blocked. Defaults to 500ms.
 * @returns { checkGuard, resetGuard, lockGuard, unlockGuard }
 */
export function useAsyncGuard(gracePeriodMs = 500) {
  const lastExecutionTimestamp = useRef<number>(0);

  /**
   * Checks if the action should proceed based on the last execution time and lock status.
   * If the guard passes, it automatically updates the internal timestamp.
   * 
   * @param key - Optional unique identifier for global deduplication.
   * @returns boolean - true if the action should proceed, false if it's blocked.
   */
  const checkGuard = useCallback((key?: string) => {
    const now = Date.now();

    // 1. Check global lock if key is provided
    if (key && globalLockedKeys.has(key)) {
      return false;
    }

    // 2. Determine last execution time (global or local)
    const lastTime = key 
      ? (globalExecutionHistory.get(key) ?? 0) 
      : lastExecutionTimestamp.current;

    // 3. Check grace period
    if (now - lastTime < gracePeriodMs) {
      return false;
    }

    // 4. Update timestamps
    if (key) {
      globalExecutionHistory.set(key, now);
    }
    lastExecutionTimestamp.current = now;

    return true;
  }, [gracePeriodMs]);

  /**
   * Manually locks a key to prevent further executions.
   */
  const lockGuard = useCallback((key: string) => {
    globalLockedKeys.add(key);
  }, []);

  /**
   * Manually unlocks a key.
   */
  const unlockGuard = useCallback((key: string) => {
    globalLockedKeys.delete(key);
  }, []);

  /**
   * Resets the guard, allowing the next call to proceed regardless of the grace period.
   * @param key - Optional unique identifier to reset globally.
   */
  const resetGuard = useCallback((key?: string) => {
    if (key) {
      globalExecutionHistory.delete(key);
      globalLockedKeys.delete(key);
    }
    lastExecutionTimestamp.current = 0;
  }, []);

  return { checkGuard, resetGuard, lockGuard, unlockGuard };
}
